import React, { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';

const NAVY = '#071b36';
const ACTIVE_BLUE = '#1265f3';

export const dashboardItem = {
  text: 'Dashboard',
  icon: DashboardRoundedIcon,
  path: '/dashboard',
  module: 'dashboard'
};

export const groups = [
  {
    label: 'OPERATIONS',
    items: [
      { text: 'Projects', icon: WorkOutlineRoundedIcon, path: '/projects', module: 'projects' },
      { text: 'Clients', icon: PeopleAltOutlinedIcon, path: '/clients', module: 'clients' },
      { text: 'Tareas y seguimiento', icon: TaskAltRoundedIcon, path: '/tasks', module: 'tasks' },
      { text: 'Calendar', icon: EventOutlinedIcon, path: '/calendar', module: 'dashboard' }
    ]
  },
  {
    label: 'SALES',
    items: [
      { text: 'Sales Documents', icon: DescriptionOutlinedIcon, path: '/sales-docs', module: 'billingDocuments' },
      { text: 'Partner Quotes', icon: HandshakeOutlinedIcon, path: '/partner-quotes', module: 'partnerQuotes' },
      { text: 'Email Center', icon: EmailOutlinedIcon, path: '/email-history', module: 'emailCenter' }
    ]
  },
  {
    label: 'FINANCE',
    items: [
      { text: 'Finance', icon: AccountBalanceWalletOutlinedIcon, path: '/finance', module: 'finance' },
      { text: 'Commissions', icon: PriceCheckOutlinedIcon, path: '/commissions', module: 'finance' },
      { text: 'Expense Tickets', icon: ReceiptLongOutlinedIcon, path: '/expense-tickets', module: 'expenseRequests' },
      { text: 'Reports', icon: AssessmentOutlinedIcon, path: '/reports', module: 'reports' }
    ]
  },
  {
    label: 'FISCAL',
    items: [
      { text: 'CFDI 4.0', icon: AccountBalanceOutlinedIcon, path: '/fiscal', module: 'fiscal', badge: 'Base' }
    ]
  },
  {
    label: 'MANAGEMENT SYSTEM',
    items: [
      { text: 'ISO 9001:2015', icon: VerifiedOutlinedIcon, path: '/iso-9001', module: 'quality' },
      { text: 'Documents', icon: FolderOutlinedIcon, path: '/documents', module: 'documents' },
      { text: 'NDA y contratos', icon: DrawOutlinedIcon, path: '/ndas', module: 'ndaAgreements' },
      { text: 'Security Center', icon: GppGoodOutlinedIcon, path: '/security-center', module: 'securityGovernance' }
    ]
  },
  {
    label: 'COLLABORATION',
    items: [
      { text: 'Chat', icon: ChatBubbleOutlineRoundedIcon, path: '/chat', module: 'chat' },
      { text: 'Notifications', icon: NotificationsNoneRoundedIcon, path: '/notifications', module: 'notifications' }
    ]
  },
  {
    label: 'SETTINGS',
    items: [
      { text: 'Users', icon: ManageAccountsOutlinedIcon, path: '/users', module: 'users' },
      { text: 'Roles & Permissions', icon: AdminPanelSettingsOutlinedIcon, path: '/roles-permissions', module: 'roles' },
      { text: 'Activity Log', icon: HistoryRoundedIcon, path: '/activity-log', module: 'activityLog' },
      { text: 'Settings', icon: SettingsOutlinedIcon, path: '/settings', module: 'settings' }
    ]
  }
];

function Brand({ collapsed }) {
  return (
    <Box sx={{ height: 64, px: collapsed ? 1 : 1.6, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 1.1, flexShrink: 0 }}>
      <Box component="img" src="/VECTOR-LOGO.png" alt="SMTLINKS" sx={{ width: collapsed ? 36 : 42, height: 36, objectFit: 'contain', objectPosition: 'left center' }} />
      {!collapsed && (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#fff', fontSize: 14.5, lineHeight: 1, fontWeight: 850, letterSpacing: '.025em' }}>SMTLINKS</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,.65)', fontSize: 8.5, mt: .45, letterSpacing: '.12em' }}>INTEGRATED MANAGEMENT</Typography>
        </Box>
      )}
    </Box>
  );
}

function NavigationItem({ item, collapsed, onNavigate, touchLayout = false }) {
  const Icon = item.icon;
  const button = (
    <ListItemButton
      component={NavLink}
      to={item.path}
      end={item.path === '/dashboard'}
      onClick={onNavigate}
      className={({ isActive }) => (isActive ? 'active' : '')}
      sx={{
        minHeight: touchLayout ? 44 : 34,
        px: collapsed ? 1 : 1.15,
        mx: .75,
        mb: .25,
        borderRadius: 1.1,
        color: 'rgba(226,235,247,.82)',
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'background-color 120ms ease, color 120ms ease',
        '&:hover': { bgcolor: 'rgba(255,255,255,.075)', color: '#fff' },
        '&.active': {
          bgcolor: ACTIVE_BLUE,
          color: '#fff',
          boxShadow: '0 5px 14px rgba(18,101,243,.27)',
          '&:hover': { bgcolor: ACTIVE_BLUE }
        }
      }}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 29, color: 'inherit', justifyContent: 'center' }}>
        <Icon sx={{ fontSize: 17 }} />
      </ListItemIcon>
      {!collapsed && <ListItemText primary={item.text} primaryTypographyProps={{ noWrap: item.path !== '/tasks', fontSize: touchLayout ? 13 : 11.5, fontWeight: 600 }} />}
      {!collapsed && item.badge && (
        <Box component="span" sx={{ ml: .5, px: .65, py: .15, borderRadius: 999, bgcolor: 'rgba(18,190,106,.18)', color: '#5ee5a2', fontSize: 8.5, fontWeight: 750 }}>
          {item.badge}
        </Box>
      )}
    </ListItemButton>
  );

  return collapsed ? <Tooltip title={item.text} placement="right" arrow>{button}</Tooltip> : button;
}

export default function ExecutiveSidebar({ mobileOpen, handleDrawerToggle, drawerWidth = 204, collapsed = false }) {
  const { hasPermission } = usePermissions();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const visibleGroups = useMemo(() => groups
    .map((group) => ({ ...group, items: group.items.filter((item) => hasPermission(item.module, 'view')) }))
    .filter((group) => group.items.length > 0), [hasPermission]);

  const dashboardVisible = hasPermission(dashboardItem.module, 'view');

  const drawerContent = (
    <Box sx={{
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      pt: { xs: 0, md: 'env(safe-area-inset-top, 0px)' },
      pb: 'env(safe-area-inset-bottom, 0px)',
      boxSizing: 'border-box',
      overflow: 'hidden',
      bgcolor: NAVY,
      color: '#fff'
    }}>
      <Brand collapsed={collapsed} />
      <Divider sx={{ borderColor: 'rgba(255,255,255,.08)' }} />
      <Box
        component="section"
        aria-label="Navigation options"
        sx={{
          flex: '1 1 0',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          touchAction: 'pan-y',
          pt: .9,
          pb: 1.25,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,.16)', borderRadius: 5 }
        }}
      >
        {dashboardVisible && <NavigationItem item={dashboardItem} collapsed={collapsed} touchLayout={mobileOpen} onNavigate={mobileOpen ? handleDrawerToggle : undefined} />}
        {visibleGroups.map((group) => (
          <Box key={group.label} sx={{ mt: 1.15 }}>
            {!collapsed && <Typography sx={{ px: 1.65, mb: .45, color: 'rgba(177,195,219,.7)', fontSize: 8.5, fontWeight: 750, letterSpacing: '.075em' }}>{group.label}</Typography>}
            <List disablePadding>
              {group.items.map((item) => <NavigationItem key={item.path} item={item} collapsed={collapsed} touchLayout={mobileOpen} onNavigate={mobileOpen ? handleDrawerToggle : undefined} />)}
            </List>
          </Box>
        ))}
      </Box>
      <Box sx={{ flexShrink: 0, borderTop: '1px solid rgba(255,255,255,.08)', p: collapsed ? .7 : 1.3 }}>
        {!collapsed && (
          <Box sx={{ mb: .75 }}>
            <Typography sx={{ color: 'rgba(255,255,255,.88)', fontSize: 9.5, fontWeight: 700 }}>SMTLINKS SOLUTIONS</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,.52)', fontSize: 9, mt: .25 }}>Fiscal year: {currentYear}</Typography>
          </Box>
        )}
        <Tooltip title="Sign out" placement="right" arrow>
          <ListItemButton
            onClick={() => { logout(); navigate('/login'); }}
            sx={{ minHeight: mobileOpen ? 44 : 32, px: collapsed ? 1 : .8, borderRadius: 1, color: 'rgba(226,235,247,.72)', justifyContent: collapsed ? 'center' : 'flex-start', '&:hover': { bgcolor: 'rgba(229,83,83,.13)', color: '#ff9b9b' } }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 27, color: 'inherit' }}><LogoutRoundedIcon sx={{ fontSize: 17 }} /></ListItemIcon>
            {!collapsed && <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 11, fontWeight: 650 }} />}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const paperSx = {
    boxSizing: 'border-box',
    width: drawerWidth,
    height: '100dvh',
    maxHeight: '100dvh',
    overflow: 'hidden',
    bgcolor: NAVY,
    borderRight: '1px solid rgba(255,255,255,.07)'
  };

  const mobilePaperSx = {
    ...paperSx,
    width: 'min(86vw, 320px)',
    maxWidth: 'calc(100vw - 24px)',
    top: 'env(safe-area-inset-top, 0px)',
    height: 'calc(100dvh - env(safe-area-inset-top, 0px))',
    maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px))'
  };

  return (
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="Primary navigation">
      <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': mobilePaperSx }}>
        {drawerContent}
      </Drawer>
      <Drawer variant="permanent" open sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { ...paperSx, transition: 'width 170ms ease' } }}>
        {drawerContent}
      </Drawer>
    </Box>
  );
}
